import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import InfoPage from ".";
import { persistAuthResult } from "../../utils/authHelpers";

const PartnerLogin = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const json = await res.json();
      if (res.ok && json.ok) {
        try { persistAuthResult({ email, token: json.token, user: json.user }); } catch (_) {}

        try {
          const subRes = await fetch('/api/access/subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email })
          });
          const subData = await subRes.json();
          const roles = (subData && subData.roles) || [];
          if (subRes.ok && subData.ok && subData.active && roles.includes('partner')) {
            window.location.assign('/partner-settings');
          } else {
            setSubmitStatus('no_access');
          }
        } catch (subError) {
          console.error('Subscription check error:', subError);
          setSubmitStatus('error');
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (_) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const belowContent = (
    <>
      <div className="login-left">
        <img src="/images/IMG_8483.jpeg" alt="Partner" className="login-image" />
        <p className="login-welcome">Welcome back</p>
      </div>

      <div className="login-right">
        <form onSubmit={handleSubmit} className="login-form">
          <h3 className="login-title">Partner Login</h3>

          {sessionExpired && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              color: '#856404',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span role="img" aria-label="warning">⚠️</span>
              Your session has expired. Please log in again.
            </div>
          )}

          <div className="login-input-group">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="login-input"
            />
            <label className="login-label">Email Address</label>
          </div>

          <div className="login-input-group">
            <div className="login-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input login-input-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label="Toggle password visibility"
                aria-pressed={showPassword}
                className="login-password-toggle"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>
            <label className="login-label">
              Password
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="login-submit">
            {isSubmitting ? 'Logging in...' : 'Login'}
          </button>

          <div className="login-forgot-link" style={{
            textAlign: 'center',
            marginTop: '12px',
          }}>
            <a href="/forgot-password" style={{
              color: '#dc1d08',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: '14px',
            }}>Forgot your password?</a>
          </div>

          <div className="login-signup-link-below">
            Not a partner yet? <a href="/partner-signup">Sign up</a>
          </div>

          <div style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '10px',
            padding: '12px 14px',
            marginTop: '12px',
            fontSize: '13px',
            color: '#0c4a6e',
            lineHeight: '1.5',
          }}>
            <strong>Just paid?</strong> Check your email for a "Set your password" link.
            If you can't find it, <a href="/forgot-password" style={{ color: '#0369a1', fontWeight: 600 }}>request a new one here</a>.
          </div>

          {submitStatus === 'error' && (
            <p className="login-error">Invalid email or password.</p>
          )}
          {submitStatus === 'no_access' && (
            <p className="login-error">
              This account does not have an active Preferred Partner subscription. Please contact Postaway support.
            </p>
          )}
        </form>
      </div>
    </>
  );

  return <InfoPage title="Partner Login" belowContent={belowContent} disableStamps />;
};

export default PartnerLogin;
